import React, { useState } from 'react';

// Auto-scrolling marquee of platform capabilities. Each card pairs a hand-
// picked photograph of Indian agriculture with the corresponding action a
// user can take on Atharva — the visual language is deliberately classic
// (Playfair Display serif, vintage tints) instead of dashboard-screenshot
// modern, because the SUBJECT of the platform is village-rooted commerce.

const CAPABILITIES = [
  {
    title: 'Onboard your FPO',
    sub: 'Setup',
    img: '/marquee/onboard-fpo.jpg',
  },
  {
    title: 'Add member farmers',
    sub: 'Roster',
    img: '/marquee/add-farmer.jpg',
  },
  {
    title: 'Publish aggregated stock',
    sub: 'Listings',
    img: '/marquee/publish-stock.jpg',
  },
  {
    title: 'Move orders through fulfilment',
    sub: 'Workflow',
    img: '/marquee/fulfilment.jpg',
  },
  {
    title: 'Discover FPOs nationwide',
    sub: 'Marketplace',
    img: '/marquee/discover.jpg',
  },
  {
    title: 'Single-FPO cart & checkout',
    sub: 'Buy',
    img: '/marquee/cart.jpg',
  },
  {
    title: 'Track every delivery',
    sub: 'Timeline',
    img: '/marquee/timeline.jpg',
  },
  {
    title: 'Chat & counter-offer',
    sub: 'Messaging',
    img: '/marquee/messaging.jpg',
  },
];

export default function HowItWorksSection() {
  const [stopScroll, setStopScroll] = useState(false);

  return (
    <section id="how" className="bg-gradient-to-b from-white to-green-50 py-20 md:py-28">
      <style>{`
        @keyframes atharvaMarquee {
          0%   { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .atharva-marquee-inner { animation: atharvaMarquee linear infinite; }
      `}</style>

      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-green-700">What you can do</p>
          <h2 className="mt-4 font-serif text-4xl font-bold leading-tight tracking-tight text-gray-900 sm:text-5xl">
            From the soil to the order ledger
          </h2>
          <p className="mt-5 font-serif text-base italic leading-relaxed text-gray-600 sm:text-lg">
            A complete operating system for FPO commerce — roster, listings, orders, chat, fulfilment.
            Buyers get a marketplace; FPOs get a workshop.
          </p>
        </div>
      </div>

      <div
        className="relative mt-14 w-full overflow-hidden"
        onMouseEnter={() => setStopScroll(true)}
        onMouseLeave={() => setStopScroll(false)}
        onTouchStart={() => setStopScroll(true)}
        onTouchEnd={() => setStopScroll(false)}
      >
        {/* Left fade — bleeds into the section's pale-green background */}
        <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-16 bg-gradient-to-r from-green-50 to-transparent md:w-32" />

        <div
          className="atharva-marquee-inner flex w-fit"
          style={{
            animationPlayState: stopScroll ? 'paused' : 'running',
            animationDuration: CAPABILITIES.length * 3500 + 'ms',
          }}
        >
          {/* Doubled so the loop is seamless — translateX(-50%) lands on the
              second copy in the exact same spot as the first frame. */}
          {[...CAPABILITIES, ...CAPABILITIES].map((c, idx) => (
            <article
              key={idx}
              className="group relative mx-3 h-96 w-64 flex-shrink-0 overflow-hidden rounded-sm bg-stone-900 shadow-lg ring-1 ring-black/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
            >
              <img
                src={c.img}
                alt={c.title}
                loading="lazy"
                className="h-full w-full scale-105 object-cover transition-transform duration-700 group-hover:scale-110"
                style={{ filter: 'sepia(0.12) saturate(0.95)' }}
              />
              {/* Dark anchor at the bottom only — bottom-half lets the title
                  read, top-half lets the photograph breathe. Plus a faint
                  warm overlay across the whole image for vintage cohesion. */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/40 to-transparent" />
              <div className="pointer-events-none absolute inset-0 bg-amber-900/10 mix-blend-multiply" />

              {/* Category eyebrow — small caps, classic serif italic */}
              <span className="absolute left-5 top-5 font-serif text-[10px] font-bold uppercase italic tracking-[0.25em] text-amber-50 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                {c.sub}
              </span>

              {/* Decorative top-left corner rule (classic editorial vibe) */}
              <span className="absolute left-5 top-12 h-px w-8 bg-amber-100/70" />

              <div className="absolute inset-x-0 bottom-0 p-5">
                <h3 className="font-serif text-xl font-bold leading-tight text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]">
                  {c.title}
                </h3>
                <span className="mt-3 inline-block h-px w-10 bg-amber-200 transition-all duration-300 group-hover:w-20" />
              </div>
            </article>
          ))}
        </div>

        {/* Right fade */}
        <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-16 bg-gradient-to-l from-green-50 to-transparent md:w-32" />
      </div>
    </section>
  );
}
